import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { Virtuoso } from 'react-virtuoso';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectProfilePic } from '@common-reducers/WhatsAppSelectors';
import { useAppDispatch } from '@common-reducers/hooks/store.hook';
import { closeBulkDashboard } from '@common-reducers/CampaignReducer';
import CampaignMessageLogRow from '../../campaign-message-log-row';
import { ContentMessageServiceActions } from '@extension/context/content/services/contentMessageServiceActions';
import { cn } from '@/lib/utils';

const isValidWhatsAppId = (id: string | undefined): boolean => {
    if (!id) {
        return false;
    }

    if (typeof id !== 'string') {
        return false;
    }

    if (!id.endsWith('@c.us') && !id.endsWith('@g.us')) {
        return false;
    }

    const numberPart = id.split('@')[0];

    if (!/^\d{10,}$/.test(numberPart)) {
        return false;
    }

    return true;
};

const getWhatsAppId = (item: any): string | undefined => {
    try {

        if (item?.contactList?.[0]?.id) {
            return item.contactList[0].id;
        }

        if (item?.message?.contactList?.[0]?.id) {
            return item.message.contactList[0].id;
        }

        if (item?.id?._serialized) {
            return item.id._serialized;
        }

        if (item?.id && typeof item.id === 'string') {
            return item.id;
        }

        const findWhatsAppId = (obj: any): string | undefined => {
            if (!obj || typeof obj !== 'object') return undefined;

            for (const value of Object.values(obj)) {
                if (typeof value === 'string' &&
                    (value.endsWith('@c.us') || value.endsWith('@g.us'))) {
                    return value;
                }
                if (typeof value === 'object') {
                    const nestedId = findWhatsAppId(value);
                    if (nestedId) return nestedId;
                }
            }
            return undefined;
        };

        return findWhatsAppId(item);
    } catch (error) {
        console.error('[getWhatsAppId] Error getting WhatsApp ID:', error);
        return undefined;
    }
};

const getContactPhone = (contact: any) => {
    if (contact?.id?.includes('@c.us')) {
        const phone = contact?.id?.replace('@c.us', '');
        return phone ? ` +${phone}` : '';
    }
    return '';
};

interface CampaignMessageListItemProps {
    item: any;
    index: number;
    isDataFromWaiting?: boolean;
}

// Per-row component: resolves its own profile pic so only the virtualized (visible) rows
// subscribe to the store — instead of the container resolving pics for ALL rows on every dispatch
function CampaignMessageListItem({
    item,
    index,
    isDataFromWaiting,
}: CampaignMessageListItemProps) {
    const dispatch = useAppDispatch();

    const whatsappId = getWhatsAppId(item);
    const hasValidWhatsAppId = !!whatsappId && isValidWhatsAppId(whatsappId);
    const profilePicUrl = useAppSelector((state) =>
        hasValidWhatsAppId ? selectProfilePic(state, whatsappId as string) : undefined
    );

    const isGroup = chatKindOf(whatsappId) === 'group';
    const contact = item?.message?.contactList?.[0] || item?.contactList?.[0];
    const contactName = (isDataFromWaiting && item.status === "Waiting In Redis")
        ? item.contactName
        : item.contactList[0].firstName;
    const status = !isDataFromWaiting ? 'Stuck' : item.status;

    const focusWAContact = () => {
        if (hasValidWhatsAppId) {
            ContentMessageServiceActions.focusContact(whatsappId as string);
            dispatch(closeBulkDashboard());
        }
    };

    return (
        <CampaignMessageLogRow
            index={index}
            profilePicUrl={profilePicUrl}
            isGroup={isGroup}
            contactName={contactName}
            contactPhone={getContactPhone(contact)}
            messageLog={item}
            onClick={focusWAContact}
            status={status}
        />
    );
}

interface CampaignMessageListContainerProps {
    isDataFromWaiting?: boolean;
    logsData?: any[];
}

export default function CampaignMessageListContainer({
    isDataFromWaiting,
    logsData: data,
}: CampaignMessageListContainerProps) {
    if (!data?.length) {
        return (
            <div className={cn(
                "p-3 text-center",
                "text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]"
            )}>
                No messages found
            </div>
        );
    }

    return (
        <div
            className={cn(
                "flex flex-col gap-1",
                "border border-[var(--color-border)] dark:border-[var(--color-border)]",
                "rounded-md overflow-y-auto overflow-x-hidden",
                "h-full max-h-[calc(100vh-420px)]",
                "p-1.5",
                "bg-[var(--color-bg)] dark:bg-[var(--color-bg)]"
            )}
        >
            <Virtuoso
                style={{ height: '400px' }}
                data={data}
                itemContent={(index, item) => (
                    <CampaignMessageListItem
                        item={item}
                        index={index}
                        isDataFromWaiting={isDataFromWaiting}
                    />
                )}
            />
        </div>
    );
}
